import React from 'react'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/shared/components/molecules/AlertDialog'

export interface ConfirmationModalProps {
	/** Whether the modal is visible */
	visible: boolean
	/** Modal title */
	title: string
	/** Modal description/warning text */
	description: string
	/** Text for the confirm button (default: "Confirm") */
	confirmText?: string
	/** Text for the cancel button (default: "Cancel") */
	cancelText?: string
	/** Button variant - 'destructive' for red button, 'default' for primary color */
	variant?: 'default' | 'destructive'
	/** Callback when confirm button is clicked */
	onConfirm: () => void | Promise<void>
	/** Callback when modal is closed (cancel or outside click) */
	onClose: () => void
	/** Loading state for async operations */
	isLoading?: boolean
	/** Loading text to show when isLoading is true (default: "Loading...") */
	loadingText?: string
}

/**
 * Reusable confirmation modal for simple confirmation actions
 * (deletions, removals, destructive actions, etc.)
 *
 * Uses AlertDialog component following the app's design patterns.
 *
 * @example
 * ```tsx
 * <ConfirmationModal
 *   visible={isOpen}
 *   title="Remove zone"
 *   description="Are you sure you want to remove this zone from the group?"
 *   confirmText="Remove"
 *   onConfirm={handleRemove}
 *   onClose={() => setIsOpen(false)}
 *   isLoading={isRemoving}
 * />
 * ```
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
	visible,
	title,
	description,
	confirmText = 'Confirm',
	cancelText = 'Cancel',
	variant = 'destructive',
	onConfirm,
	onClose,
	isLoading = false,
	loadingText = 'Loading...',
}) => {
	return (
		<AlertDialog open={visible} onOpenChange={onClose}>
			<AlertDialogContent className="sm:max-w-[500px]">
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{description}</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isLoading}>
						{cancelText}
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={(e) => {
							e.preventDefault()
							onConfirm()
						}}
						disabled={isLoading}
						className={
							variant === 'destructive'
								? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
								: ''
						}
					>
						{isLoading ? loadingText : confirmText}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}

export default ConfirmationModal
